import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Subscription plans for FamilyForLife.
 * Price IDs are server-side only (functions/.env) — never stored in the frontend.
 */
export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: 0,
    annualPrice: 0,
    interval: null,
    seats: 0,
    features: ['basic_messaging', 'family_tree', 'events', 'announcements'],
  },
  INDIVIDUAL: {
    id: 'individual',
    name: 'Solo',
    price: 799, // cents ($7.99/mo)
    annualPrice: 7999,
    interval: 'month',
    seats: 1,
    upgradesTo: null, // top of individual path
    features: [
      'basic_messaging', 'family_tree', 'events', 'announcements',
      'documents', 'photos', 'vault', 'recipes', 'health_updates',
      'emergency_info', 'goals',
    ],
  },
  COUPLE: {
    id: 'couple',
    name: 'Couples',
    price: 1000, // cents ($10.00/mo)
    annualPrice: 9999,
    interval: 'month',
    seats: 2,
    upgradesTo: 'family',
    features: [
      'basic_messaging', 'family_tree', 'events', 'announcements',
      'documents', 'photos', 'vault', 'recipes', 'health_updates',
      'emergency_info', 'goals', 'directory',
    ],
  },
  FAMILY: {
    id: 'family',
    name: 'Family',
    price: 1500, // cents ($15.00/mo)
    annualPrice: 14999,
    interval: 'month',
    seats: 5,
    upgradesTo: null, // uses additional_member add-on
    features: [
      'basic_messaging', 'family_tree', 'events', 'announcements',
      'documents', 'photos', 'vault', 'recipes', 'health_updates',
      'emergency_info', 'goals', 'directory', 'advanced_permissions',
    ],
  },
};

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  SUSPENDED: 'suspended',
  TRIALING: 'trialing',
  INCOMPLETE: 'incomplete',
};

// ─── helper: get Firebase callable functions ────────────────────────────────
function getCallable(name) {
  return httpsCallable(getFunctions(), name);
}

/**
 * Service for managing subscriptions (Stripe-backed).
 *
 * Data model on /users/{uid}:
 *   subscription: {
 *     planId, billingInterval, status,
 *     stripeCustomerId, stripeSubscriptionId,
 *     currentPeriodStart, currentPeriodEnd,
 *     cancelAtPeriodEnd, trialEnd, gracePeriodEnd
 *   }
 *
 * Seat tracking on /families/{familyCode}:
 *   seats: [{ id, assignedTo, status, assignedAt }]
 *   totalSeats, usedSeats, subscriptionPlanId
 */
class SubscriptionService {

  // ── getters ──────────────────────────────────────────────────────────────

  async getSubscription(userId) {
    if (!userId) return null;
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (!userSnap.exists()) return null;
    const data = userSnap.data();
    const subscription = data.subscription || { planId: 'free', status: SUBSCRIPTION_STATUS.ACTIVE };
    const plan = Object.values(SUBSCRIPTION_PLANS).find(p => p.id === subscription.planId)
      || SUBSCRIPTION_PLANS.FREE;
    return { ...subscription, plan };
  }

  async getSeatUsage(familyCode) {
    if (!familyCode) return null;
    const familySnap = await getDoc(doc(db, 'families', familyCode));
    if (!familySnap.exists()) return null;
    const data = familySnap.data();
    return {
      seats: data.seats || [],
      totalSeats: data.totalSeats || 0,
      usedSeats: data.usedSeats || 0,
      planId: data.subscriptionPlanId || 'free',
    };
  }

  /**
   * Assign an available seat to a family member.
   * Finds the first unassigned seat and assigns it to the given user.
   * @param {string} familyCode
   * @param {string} userId - UID of the member receiving the seat
   * @returns {Promise<{success: boolean, seatId: string|null}>}
   */
  async assignSeat(familyCode, userId) {
    if (!familyCode || !userId) throw new Error('familyCode and userId are required');
    const familyRef = doc(db, 'families', familyCode);
    const familySnap = await getDoc(familyRef);
    if (!familySnap.exists()) throw new Error('Family not found');

    const data = familySnap.data();
    const seats = Array.isArray(data.seats) ? [...data.seats] : [];

    // Check user doesn't already hold a seat
    if (seats.some(s => s.assignedTo === userId && s.status === 'active')) {
      return { success: false, seatId: null, reason: 'User already has a seat' };
    }

    const idx = seats.findIndex(s => s.status !== 'active' || !s.assignedTo);
    if (idx === -1) return { success: false, seatId: null, reason: 'No seats available' };

    seats[idx] = { ...seats[idx], assignedTo: userId, status: 'active', assignedAt: new Date() };
    const usedSeats = seats.filter(s => s.status === 'active').length;

    await updateDoc(familyRef, { seats, usedSeats, updatedAt: new Date() });
    return { success: true, seatId: seats[idx].id };
  }

  /**
   * Revoke a seat currently held by a family member.
   * @param {string} familyCode
   * @param {string} userId - UID of the member whose seat is being revoked
   * @returns {Promise<{success: boolean}>}
   */
  async revokeSeat(familyCode, userId) {
    if (!familyCode || !userId) throw new Error('familyCode and userId are required');
    const familyRef = doc(db, 'families', familyCode);
    const familySnap = await getDoc(familyRef);
    if (!familySnap.exists()) throw new Error('Family not found');

    const data = familySnap.data();
    const seats = Array.isArray(data.seats) ? [...data.seats] : [];

    const idx = seats.findIndex(s => s.assignedTo === userId);
    if (idx === -1) return { success: false, reason: 'User has no seat' };

    seats[idx] = { ...seats[idx], assignedTo: null, status: 'unassigned', assignedAt: null };
    const usedSeats = seats.filter(s => s.status === 'active').length;

    await updateDoc(familyRef, { seats, usedSeats, updatedAt: new Date() });
    return { success: true };
  }

  async getBillingHistory(userId) {
    if (!userId) return [];
    const snap = await getDocs(collection(db, 'users', userId, 'invoices'));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  }

  // ── status helpers ────────────────────────────────────────────────────────

  isTrialActive(subscription) {
    if (!subscription) return false;
    if (subscription.status !== SUBSCRIPTION_STATUS.TRIALING) return false;
    if (!subscription.trialEnd) return false;
    const ms = typeof subscription.trialEnd.toMillis === 'function'
      ? subscription.trialEnd.toMillis()
      : new Date(subscription.trialEnd).getTime();
    return ms > Date.now();
  }

  trialDaysRemaining(subscription) {
    if (!this.isTrialActive(subscription)) return 0;
    const ms = typeof subscription.trialEnd.toMillis === 'function'
      ? subscription.trialEnd.toMillis()
      : new Date(subscription.trialEnd).getTime();
    return Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24));
  }

  isInGracePeriod(subscription) {
    if (!subscription) return false;
    if (subscription.status !== SUBSCRIPTION_STATUS.PAST_DUE) return false;
    if (!subscription.gracePeriodEnd) return false;
    const ms = typeof subscription.gracePeriodEnd.toMillis === 'function'
      ? subscription.gracePeriodEnd.toMillis()
      : new Date(subscription.gracePeriodEnd).getTime();
    return ms > Date.now();
  }

  graceDaysRemaining(subscription) {
    if (!this.isInGracePeriod(subscription)) return 0;
    const ms = typeof subscription.gracePeriodEnd.toMillis === 'function'
      ? subscription.gracePeriodEnd.toMillis()
      : new Date(subscription.gracePeriodEnd).getTime();
    return Math.ceil((ms - Date.now()) / (1000 * 60 * 60 * 24));
  }

  /**
   * Returns true if the user has full app access (active paid, trial, or grace period).
   */
  isAccessGranted(subscription) {
    if (!subscription) return false;
    if (this.isTrialActive(subscription)) return true;
    if (this.isInGracePeriod(subscription)) return true; // limited mode, but still let them in
    return subscription.status === SUBSCRIPTION_STATUS.ACTIVE;
  }

  /**
   * True when Stripe has a live subscription that is paying or in a valid trial window.
   * A saved customer + card alone is NOT enough — Customer Portal cannot start a brand-new
   * subscription; users must complete Checkout (createCheckoutSession) for that.
   */
  hasActiveStripeSubscription(subscription) {
    if (!subscription?.stripeSubscriptionId) return false;
    if (subscription.status === SUBSCRIPTION_STATUS.ACTIVE) return true;
    if (subscription.status === SUBSCRIPTION_STATUS.TRIALING && this.isTrialActive(subscription)) return true;
    return false;
  }

  // ── Stripe checkout ───────────────────────────────────────────────────────

  /**
   * Create a Stripe Checkout session via Cloud Function.
   * Returns { url } — caller should redirect to url.
   */
  async createCheckoutSession(userId, planId) {
    if (!userId || !planId) throw new Error('User ID and plan ID are required');
    const fn = getCallable('createCheckoutSession');
    const result = await fn({ planId });
    return result.data; // { url, sessionId }
  }

  /**
   * Create a Stripe Customer Portal session via Cloud Function.
   * Returns { url } — caller should redirect to url.
   */
  async createPortalSession(userId) {
    if (!userId) throw new Error('User ID is required');
    const fn = getCallable('createPortalSession');
    const result = await fn({});
    return result.data; // { url }
  }

  // ── trial init (new family owners) ───────────────────────────────────────

  async initializeFreeSubscription(userId) {
    if (!userId) return;
    const now = new Date();
    const trialEnd = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    await updateDoc(doc(db, 'users', userId), {
      subscription: {
        planId: 'family',
        billingInterval: null,
        status: SUBSCRIPTION_STATUS.TRIALING,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        currentPeriodStart: now,
        currentPeriodEnd: trialEnd,
        cancelAtPeriodEnd: false,
        trialEnd,
        gracePeriodEnd: null,
      },
      updatedAt: new Date(),
    });
  }

  // ── seat helpers ──────────────────────────────────────────────────────────

  /**
   * Claim an unassigned seat for a new member.
   * Called when an invite is accepted with sponsorship = true.
   */
  async claimSeat(familyCode, userId) {
    if (!familyCode || !userId) return false;
    const familyRef = doc(db, 'families', familyCode);
    const familySnap = await getDoc(familyRef);
    if (!familySnap.exists()) return false;

    const data = familySnap.data();
    const seats = [...(data.seats || [])];
    const idx = seats.findIndex(s => s.status === 'unassigned' || s.status === 'pending');
    if (idx === -1) return false;

    seats[idx] = { ...seats[idx], assignedTo: userId, status: 'active', assignedAt: new Date() };
    const usedSeats = seats.filter(s => s.status === 'active').length;
    await updateDoc(familyRef, { seats, usedSeats, updatedAt: new Date() });
    return true;
  }

  /**
   * Release a seat when a member is removed.
   */
  async releaseSeat(familyCode, userId) {
    if (!familyCode || !userId) return;
    const familyRef = doc(db, 'families', familyCode);
    const familySnap = await getDoc(familyRef);
    if (!familySnap.exists()) return;

    const data = familySnap.data();
    const seats = (data.seats || []).map(s =>
      s.assignedTo === userId
        ? { ...s, assignedTo: null, status: 'unassigned', assignedAt: null }
        : s,
    );
    const usedSeats = seats.filter(s => s.status === 'active').length;
    await updateDoc(familyRef, { seats, usedSeats, updatedAt: new Date() });
  }

  // ── feature access ────────────────────────────────────────────────────────

  async hasFeatureAccess(userId, feature) {
    const subscription = await this.getSubscription(userId);
    if (!subscription) return false;
    if (subscription.status === SUBSCRIPTION_STATUS.SUSPENDED) return false;
    return subscription.plan.features.includes(feature);
  }
}

export const subscriptionService = new SubscriptionService();
